import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Spacer,
  Box,
  Text,
  Heading,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { ReactElement, useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { PortfolioChartDatePicker } from '../shared/PortfolioChartDatePicker';
import {
  ChartTimePeriod,
  cohortCadenceLabelFormatter,
  CohortConfig,
  defaultCohortCadence,
  defaultMaxSeriesAge,
  formatCohortCadenceSelectOptions,
  onToggleActiveField,
  PortfolioChartConfig,
  ProfitsVintageInsight,
  ProfitsVintageTableRow,
} from '../shared/portfolioChartUtils';
import { PortfolioVintageChartCards } from '../shared/PortfolioVintageChartCards';
import { PortfolioVintageReChart } from '../shared/PortfolioVintageReChart';
import { ProfitLossVintageTable } from './ProfitLossVintageTable';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { PortfolioDownloadCsvLink } from 'packages/portfolio/ui/shared/PortfolioDownloadCsvLink';

type VintageChartTemplateProps = {
  setSelectedMaxSeriesAge: React.Dispatch<React.SetStateAction<number>>;
  tableDataByOriginationDate: ProfitsVintageTableRow[];
  selectedCohortConfig: CohortConfig;
  setSelectedCohortConfig: React.Dispatch<React.SetStateAction<CohortConfig>>;
  chartTitle: string;
  activeFields: Partial<keyof ProfitsVintageInsight>[];
  setActiveFields: React.Dispatch<
    React.SetStateAction<Partial<keyof ProfitsVintageInsight>[]>
  >;
  selectedDatapoint: ProfitsVintageInsight | null;
  setSelectedDatapoint: React.Dispatch<
    React.SetStateAction<ProfitsVintageInsight | null>
  >;
  yAxisLabel?: string;
  chartConfig: PortfolioChartConfig<string>;
  chartDataWithCohortCadence: ProfitsVintageInsight[];
  chartDataDateRange: {
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  };
  // eslint-disable-next-line react/no-unused-prop-types
  setChartDataDateRange: React.Dispatch<
    React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>
  >;
  fieldType: CreditAgreement.DataTapeField.Type;
  onSelectDateRangeHandler: (newDateRange: [Date | null, Date | null]) => void;
  showOriginatedReceivablesCount: boolean;
  chartTypeSelector?: ReactElement;
};
export const VintageChartTemplate = ({
  setSelectedMaxSeriesAge,
  tableDataByOriginationDate,
  selectedCohortConfig,
  setSelectedCohortConfig,
  chartTitle,
  activeFields,
  setActiveFields,
  selectedDatapoint,
  yAxisLabel,
  chartConfig,
  setSelectedDatapoint,
  chartDataWithCohortCadence,
  chartDataDateRange,
  fieldType,
  onSelectDateRangeHandler,
  showOriginatedReceivablesCount,
  chartTypeSelector,
}: VintageChartTemplateProps) => {
  const doesDataForChartExist = chartDataWithCohortCadence.some(
    (cohortSeries) => {
      return Object.entries(cohortSeries).some(([key, value]) => {
        return key !== 'cohortSeriesAge' && value !== null;
      });
    },
  );

  const cohortCadenceSelectOptions = useMemo(
    () => formatCohortCadenceSelectOptions(),
    [],
  );

  const PortfolioChartCardsInstance = PortfolioVintageChartCards<ProfitsVintageInsight>();
  const PortfolioChartInstance = PortfolioVintageReChart<ProfitsVintageInsight>();

  const downloadFileHeaders =
    tableDataByOriginationDate.length > 0
      ? _.map(_.keys(tableDataByOriginationDate[0]), (headerKey) => {
          return {
            label: commonUtils.toTitleCase(headerKey),
            key: headerKey,
          };
        })
      : undefined;

  const formattedDownloadData = tableDataByOriginationDate.map((row) => {
    const newRow = { ...row };
    newRow.originationDate = cohortCadenceLabelFormatter(
      selectedCohortConfig.cohortBaseCadence,
      newRow.originationDate,
    ) as Util.FullDateStr;
    return newRow;
  });

  return (
    <Box mb="40px">
      <Flex
        backgroundColor="white"
        borderRadius="8px"
        borderWidth="1px"
        borderColor={colors.gray[200]}
        padding="40px"
        direction="column"
      >
        <Flex>
          <Box width={doesDataForChartExist ? '70%' : '100%'}>
            <Flex justifyContent="space-between" mb="24px" alignItems="center">
              <Heading as="h3" fontSize="20px">
                {chartTitle}
              </Heading>
              {chartTypeSelector}
            </Flex>
            <SimpleGrid columns={2} spacing="24px" mb="40px">
              <Box
                border={`1px solid ${colors.gray[200]}`}
                borderRadius="8px"
                padding="16px"
              >
                <Text fontWeight={500} mb="16px">
                  Vintage
                </Text>

                <Select
                  value={selectedCohortConfig.cohortBaseCadence}
                  onChange={(cadence) =>
                    setSelectedCohortConfig({
                      ...selectedCohortConfig,
                      cohortBaseCadence: cadence ?? defaultCohortCadence,
                    })
                  }
                  options={cohortCadenceSelectOptions}
                />
                <Box h="16px" />
                <PortfolioChartDatePicker
                  onSelectDateRange={onSelectDateRangeHandler}
                  currentDateRange={chartDataDateRange}
                  width="100%"
                />
              </Box>
              <Box
                border={`1px solid ${colors.gray[200]}`}
                borderRadius="8px"
                padding="16px"
              >
                <Text fontWeight={500} mb="16px">
                  Timeline
                </Text>
                {/* positive integers only */}
                <NumberInput
                  step={1}
                  defaultValue={12}
                  min={0}
                  precision={0}
                  mb="16px"
                  onChange={(value) => {
                    setSelectedMaxSeriesAge(_.toNumber(value));
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper>
                      <Icon
                        icon="caret-up"
                        type="solid"
                        color={colors.gray[400]}
                      />
                    </NumberIncrementStepper>
                    <NumberDecrementStepper>
                      <Icon
                        icon="caret-down"
                        type="solid"
                        color={colors.gray[400]}
                      />
                    </NumberDecrementStepper>
                  </NumberInputStepper>
                </NumberInput>
                <Select
                  value={selectedCohortConfig.cohortSeriesCadence}
                  onChange={(cadence) =>
                    setSelectedCohortConfig({
                      ...selectedCohortConfig,
                      cohortSeriesCadence: cadence ?? defaultCohortCadence,
                    })
                  }
                  options={cohortCadenceSelectOptions}
                />
              </Box>
            </SimpleGrid>
            {!doesDataForChartExist && (
              <Alert status="warning" borderRadius="6px">
                <AlertIcon />
                <AlertDescription fontWeight="semibold">
                  Data is not available for the selected date range. Select
                  another date range.
                </AlertDescription>
              </Alert>
            )}
            {doesDataForChartExist && (
              <PortfolioChartInstance
                chartData={chartDataWithCohortCadence}
                chartTitle={chartTitle}
                selectedCohortSeriesCadence={
                  selectedCohortConfig.cohortSeriesCadence
                }
                currentSelectedCohortSeriesAge={
                  selectedDatapoint
                    ? selectedDatapoint.cohortSeriesAge
                    : defaultMaxSeriesAge
                }
                onSelectDatapoint={setSelectedDatapoint}
                chartConfig={chartConfig}
                visibleChartComponents={activeFields}
                yAxisLabel={yAxisLabel}
                showLegend={false}
              />
            )}
          </Box>
          <Spacer />
          {selectedDatapoint && (
            <Flex width="25%" flexDirection="column">
              <PortfolioChartCardsInstance
                selectedDatapoint={selectedDatapoint}
                chartConfig={chartConfig}
                cohortSeriesCadence={selectedCohortConfig.cohortSeriesCadence}
                activeFields={activeFields}
                onToggleActiveField={(
                  selectedField: keyof ProfitsVintageInsight,
                ) =>
                  onToggleActiveField<ProfitsVintageInsight>({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
                chartTitle={chartTitle}
              />

              {downloadFileHeaders && (
                <Flex justifyContent="flex-end">
                  <PortfolioDownloadCsvLink
                    dataRows={formattedDownloadData}
                    headers={downloadFileHeaders}
                    chartTitle={chartTitle}
                    selectedTimePeriod={
                      (selectedCohortConfig.cohortBaseCadence as unknown) as ChartTimePeriod
                    }
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
        {doesDataForChartExist && (
          <Box mt="40px">
            <ProfitLossVintageTable
              tableData={tableDataByOriginationDate}
              selectedCohortConfig={selectedCohortConfig}
              maxSeriesAge={chartDataWithCohortCadence.length}
              fieldType={fieldType}
              showOriginatedReceivablesCount={showOriginatedReceivablesCount}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
